/**
 * Breadcrumbs — the PRODUCT trails: appliances, parts and filters (FRIDGE-269
 * Phase 1; the taxonomy trail of FRIDGE-57).
 *
 * The same builder as lib/breadcrumbs.ts and lib/breadcrumbs-catalog.ts — same
 * roots, same types, same rules. It is its own module because the PDP client
 * (app/products/[slug]/product-client.tsx) imports it for the client-only
 * fallback trail: the collection and hub trails in lib/breadcrumbs-catalog.ts
 * need the collection URL parser (lib/collection-url-parser.ts and the
 * use-case table behind it), which no product page needs in its browser
 * bundle. lib/breadcrumbs-catalog.ts re-exports everything here, so server
 * callers keep one import.
 *
 * The approved trail (FRIDGE-57, recommendation 2): a product's trail stops at
 * its type, under its spine:
 *
 *   Fridge.com › Refrigerators › French Door Refrigerators › {product}
 *   Fridge.com › Coolers & Beverage › Wine Fridges › {product}
 *
 * The type is the canonical collection the product's type (then its title)
 * names, followed through one fold of a retired head (wine-coolers → Wine
 * Fridges), and only when that collection is a node of the approved tree
 * (lib/taxonomy/tree.ts). Otherwise the appliance family the product's own words
 * name (Refrigerators / Freezers), then the spine of the named family; when
 * nothing maps the level is OMITTED rather than invented: `Fridge.com / {product}`.
 *
 * A PART or a FILTER (lib/parts/fpn.ts) is not a product of any collection:
 * `Fridge.com / Parts / {part}` and `Fridge.com / Filters / {filter}`.
 *
 * Guards: tests/taxonomy-trails-fridge-57.test.ts (every page type against the
 * packet's 266-node tree), tests/breadcrumbs-helpers.test.ts,
 * tests/breadcrumbs-visible-equals-schema.test.ts, tests/breadcrumbs-source-pin.test.ts.
 */

import { canonicalSlugForTypeOrSlug, displayNameForCanonicalSlug, inferCanonicalSlugFromText } from '@/lib/collections'
import { formatCollectionName, formatProductTitle } from '@/lib/breadcrumb-utils'
import { HOME_CRUMB, SECTION_ROOTS, type BreadcrumbTrail, type Crumb } from '@/lib/breadcrumbs'
import { isFilterType, isTruePartType } from '@/lib/parts/fpn'
import { FAMILY_SPINE, collectionNodeLevels, collectionNodeSlug, partTypeNode, spineLevel, type TaxonomyLevel } from '@/lib/taxonomy/tree'

function clean(s: string | null | undefined): string {
  return (s ?? '').trim()
}

// ---------------------------------------------------------------------------
// Taxonomy levels → named crumbs (shared with lib/breadcrumbs-catalog.ts)
// ---------------------------------------------------------------------------

/**
 * Named, linked crumbs for taxonomy levels. A spine page carries the tree's name
 * (Coolers & Beverage); a collection node carries the tree's name for it, its
 * PLURAL canonical display name (lib/collections displayNameForCanonicalSlug,
 * then formatCollectionName), and its name and url come from the one slug, so
 * they can never disagree (FRIDGE-271).
 */
export function taxonomyCrumbs(levels: ReadonlyArray<TaxonomyLevel>): Crumb[] {
  return levels.map((level) =>
    level.kind === 'spine'
      ? { name: level.name, url: level.url }
      : { name: displayNameForCanonicalSlug(level.slug) || formatCollectionName(level.slug), url: `/collections/${level.slug}` },
  )
}

/** The linked levels above a page that belongs to a collection node (the node included). */
export function collectionNodeCrumbs(nodeSlug: string): Crumb[] {
  return taxonomyCrumbs(collectionNodeLevels(nodeSlug))
}

// ---------------------------------------------------------------------------
// Products (PDP)
// ---------------------------------------------------------------------------

/**
 * Canonical family slugs that name a WHOLE appliance family. When the type
 * resolves to one of these, a more specific canonical inferred from the title
 * wins ("refrigerators" → "counter-depth-refrigerators"), matching the chain
 * app/products/[slug]/page.tsx has used for the schema crumb since FRIDGE-271.
 */
const GENERIC_FAMILIES = new Set(['refrigerators', 'freezers'])

/**
 * Product types that are PARTS, not appliances — the ONE definition lives in
 * lib/parts/fpn.ts (PART_PRODUCT_TYPES: the filter types, gasket, sensor and
 * the staged 'part'). FRIDGE-333: a part's trail is `Fridge.com / Parts /
 * {part}` (partBreadcrumbs below) — never an appliance family, never a
 * collection — because a part lives at /parts/<slug>, not under a collection.
 * The two legacy vocabulary words ('accessory', 'accessories') are kept out of
 * the family fallback for the same reason.
 */
const NON_APPLIANCE_TYPES = new Set(['parts', 'accessory', 'accessories'])

/**
 * The canonical collection slug for a product, from its type first (with a
 * plural retry so "refrigerator" → "refrigerators"), then the title when the
 * type is generic or unmapped. Null when nothing maps.
 */
export function productCollectionSlug(product: {
  type?: string | null
  title?: string | null
}): string | null {
  const type = clean(product.type)
  const title = clean(product.title)
  const canonFromType =
    canonicalSlugForTypeOrSlug(type) || (type ? canonicalSlugForTypeOrSlug(`${type}s`) : null)
  const canonFromTitle = inferCanonicalSlugFromText(title)
  if (canonFromTitle && (!canonFromType || GENERIC_FAMILIES.has(canonFromType))) return canonFromTitle
  return canonFromType || null
}

/**
 * The appliance family a product belongs to when no canonical collection
 * maps — decided ONLY from words the product's own type/title contain.
 * "refrigerator"/"fridge" wins over "freezer" (a fridge with a freezer
 * compartment is a refrigerator); "freezerless" contains no whole word
 * "freezer" and correctly lands on Refrigerators. Parts types never map.
 */
export function productFamilySlug(product: {
  type?: string | null
  title?: string | null
}): 'refrigerators' | 'freezers' | null {
  const type = clean(product.type).toLowerCase()
  if (isFilterType(type) || isTruePartType(type) || NON_APPLIANCE_TYPES.has(type)) return null
  const text = `${type} ${clean(product.title)}`.toLowerCase().replace(/[_-]+/g, ' ')
  if (/\b(refrigerators?|fridges?)\b/.test(text)) return 'refrigerators'
  if (/\bfreezers?\b/.test(text)) return 'freezers'
  return null
}

/**
 * The leaf crumb prints the page's H1 label when the caller supplies it
 * (FRIDGE-1031, part detail row, 09/17/2026). A part or filter's <h1> is the
 * honest heading built from what it IS (FRIDGE-538: brand + type + part number
 * or "for <OEM filter>"), while `title` is the marketplace string it was
 * imported with — so the trail said "MaksPro WR24X10147 Door Gasket Seal
 * Compatible with GE …" under an H1 that said something else. `title` still
 * drives everything else (collection resolution, the appliance leaf); only the
 * printed leaf follows the heading, and only when a heading was supplied.
 */
function leafCrumbName(product: { title?: string | null; leafName?: string | null }): string {
  const heading = (product.leafName ?? '').trim()
  return heading || formatProductTitle(clean(product.title))
}

/**
 * /part/[slug] — `Fridge.com / Parts / {Part}` (FRIDGE-333 · FRIDGE-389). The
 * leaf is the part's formatted title, exactly as the PDP leaf is; the middle
 * crumb is the parts store root (lib/breadcrumbs.ts SECTION_ROOTS.parts),
 * never an appliance family and never a collection. A part type with a node
 * under /parts (lib/taxonomy/tree.ts PART_TYPE_NODES, recommendation 13 of the
 * FRIDGE-57 taxonomy: Ice-Maker Kits) adds that node between the root and the
 * leaf: `Fridge.com / Parts / Ice-Maker Kits / {kit}`.
 */
export function partBreadcrumbs(part: { title?: string | null; leafName?: string | null; type?: string | null }): BreadcrumbTrail {
  const node = partTypeNode(part.type)
  const leaf: Crumb = { name: leafCrumbName(part) }
  return node ? [HOME_CRUMB, SECTION_ROOTS.parts, { name: node.name, url: node.url }, leaf] : [HOME_CRUMB, SECTION_ROOTS.parts, leaf]
}

/**
 * /filter/[slug] — `Fridge.com / Filters / {Filter}` (FRIDGE-396: a filter is
 * not a part; its middle crumb is the filter STORE root at /filter, never the
 * /filters editorial hub family and never the parts store).
 */
export function filterProductBreadcrumbs(filter: { title?: string | null; leafName?: string | null }): BreadcrumbTrail {
  return [HOME_CRUMB, SECTION_ROOTS.filterStore, { name: leafCrumbName(filter) }]
}

/**
 * The linked levels above a product, root first (FRIDGE-57 recommendation 2):
 * its type node under its spine; else the appliance family the product's own
 * words name; else the spine of the family it names; else none.
 */
export function productTrailParents(product: { type?: string | null; title?: string | null }): Crumb[] {
  const canon = productCollectionSlug(product)
  const node = collectionNodeSlug(canon)
  if (node) return collectionNodeCrumbs(node)
  const family = productFamilySlug(product)
  if (family) return collectionNodeCrumbs(family)
  const spine = canon ? FAMILY_SPINE[canon] : undefined
  const level = spine ? spineLevel(spine) : null
  return level ? taxonomyCrumbs([level]) : []
}

/**
 * /products/[slug] — `Fridge.com / {Spine} / {Type} / {Product}` (FRIDGE-57).
 * Every collection crumb's name and url derive from the SAME slug so they can
 * never disagree (FRIDGE-271); names are plural through formatCollectionName
 * and no crumb links /collections/all. When nothing maps, the trail is
 * `Fridge.com / {Product}` — omitted, not invented.
 *
 * A PART or a FILTER (lib/parts/fpn.ts) is not a product of any collection:
 * its trail is partBreadcrumbs() / filterProductBreadcrumbs() — on both the
 * server (page.tsx) and the client-only fallback (product-client.tsx), which
 * both call this one function.
 */
export function productBreadcrumbs(product: {
  type?: string | null
  title?: string | null
  /** The page's H1 label; honored on the part and filter trails only (see leafCrumbName). */
  leafName?: string | null
}): BreadcrumbTrail {
  if (isFilterType(product.type)) return filterProductBreadcrumbs(product)
  if (isTruePartType(product.type)) return partBreadcrumbs(product)
  const leaf: Crumb = { name: formatProductTitle(clean(product.title)) }
  return [HOME_CRUMB, ...productTrailParents(product), leaf]
}
